import { TAutoReplyMessageAsset } from 'api';
import { EContentSectionType, TMessageSection } from 'types';
import { v4 as uuidv4 } from 'uuid';

type CreateSectionOptions = {
  assets?: TAutoReplyMessageAsset[];
};

export const createSection = (
  type: EContentSectionType,
  _options?: CreateSectionOptions,
): TMessageSection => {
  const id = uuidv4();

  switch (type) {
    case EContentSectionType.TEXT:
      return { id, type, content: '' };
    case EContentSectionType.IMAGE:
      return { id, type, file: null };
    case EContentSectionType.VIDEO:
      return { id, type, file: null };
    case EContentSectionType.FILE:
      return { id, type, file: null };
    case EContentSectionType.RICH_MESSAGE:
    case EContentSectionType.CARD_TYPE_MESSAGE: {
      // 素材の並び順に依存して意図しないものを送らないよう、必ず明示選択させる。
      return { id, type };
    }
    case EContentSectionType.SURVEY:
      return { id, type, content: '' };
  }
};

export const createDefaultAutoReplySections = (): TMessageSection[] => [
  createSection(EContentSectionType.TEXT),
];

export const isSectionFilled = (section: TMessageSection): boolean => {
  if (!section.type) return false;

  switch (section.type) {
    case EContentSectionType.TEXT:
      return !!section.content.trim();

    case EContentSectionType.IMAGE:
    case EContentSectionType.VIDEO:
    case EContentSectionType.FILE:
      return !!section.file || !!section.originalMedia;

    case EContentSectionType.RICH_MESSAGE:
    case EContentSectionType.CARD_TYPE_MESSAGE:
      return !!section.assetId;

    case EContentSectionType.SURVEY:
      return (
        Number.isInteger(section.surveyId) && (section.surveyId ?? 0) > 0
      );

    default:
      return false;
  }
};

export const hasAnyContent = (sections: TMessageSection[]): boolean =>
  sections.some(isSectionFilled);

export const areSurveySectionsValid = (sections: TMessageSection[]): boolean =>
  sections.every(
    (section) =>
      section.type !== EContentSectionType.SURVEY || isSectionFilled(section),
  );

export const hasFilledReplySection = (sections: TMessageSection[]): boolean =>
  sections.some(isSectionFilled);

const getExpectedAssetType = (
  sectionType: EContentSectionType,
): TAutoReplyMessageAsset['type'] | null => {
  if (sectionType === EContentSectionType.RICH_MESSAGE) return 'RICH_MESSAGE';
  if (sectionType === EContentSectionType.CARD_TYPE_MESSAGE)
    return 'CARD_TYPE_MESSAGE';
  return null;
};

/**
 * 素材セクションがすべて、取得済みの同種素材を明示的に参照しているか検証する。
 * 素材を含まないメッセージは、素材一覧の取得状態に関係なく有効。
 */
export const areMessageAssetSectionsValid = (
  sections: TMessageSection[],
  assets: TAutoReplyMessageAsset[],
  assetsReady: boolean,
): boolean => {
  const assetSections = sections.filter(
    (section) => getExpectedAssetType(section.type) !== null,
  );

  if (assetSections.length === 0) return true;
  if (!assetsReady) return false;

  return assetSections.every((section) => {
    const expectedType = getExpectedAssetType(section.type);
    const assetId = 'assetId' in section ? section.assetId : undefined;

    if (
      expectedType === null ||
      !Number.isInteger(assetId) ||
      (assetId ?? 0) <= 0
    ) {
      return false;
    }

    return assets.some(
      (asset) => asset.id === assetId && asset.type === expectedType,
    );
  });
};
